import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Link as MuiLink,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VestingSchedulePopup from "../../Common/VestingSchedulePopup";

const darkColors = {
  background: "#121212",
  paper: "#1e1e1e",
  text: "#ffffff",
  textSecondary: "#b0b0b0",
  primary: "#90caf9",
  border: "#333333",
};

const lightColors = {
  background: "#ffffff",
  paper: "#f8f9fa",
  text: "#212529",
  textSecondary: "#6c757d",
  primary: "#1976d2",
  border: "#dee2e6",
};

/**
 * TermsAgreementPopup
 *
 * Props:
 *   open            - boolean
 *   onClose         - function
 *   onProceed       - function
 *   links           - array of { name, url }
 *   theme           - "light" | "dark"
 *   vestingCurrency - string  e.g. "CO2COIN" — when provided shows the vesting schedule checkbox
 *   txnType         - number | string  1 = buy; vesting checkbox only shown for buy transactions
 */
const TermsAgreementPopup = ({
  open,
  onClose,
  onProceed,
  links = [],
  theme = "light",
  vestingCurrency,
  txnType,
}) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const colors = theme === "dark" ? darkColors : lightColors;

  const [agreed, setAgreed] = useState(false);
  const [vestingAgreed, setVestingAgreed] = useState(false);
  const [vestingPopupOpen, setVestingPopupOpen] = useState(false);

  // Vesting section only shown for buy transactions (txnType == 1)
  const showVestingSection = !!vestingCurrency && txnType == 1;

  useEffect(() => {
    if (open) {
      setAgreed(false);
      setVestingAgreed(false);
    }
  }, [open]);

  const canProceed = agreed && (!showVestingSection || vestingAgreed);

  const handleProceed = () => {
    if (!canProceed) return;
    onClose();
    if (typeof onProceed === "function") {
      onProceed();
    }
  };

  const renderAgreementLabel = () => {
    const safeLinks = Array.isArray(links) ? links.filter((l) => l && l.name && l.url) : [];
    if (safeLinks.length === 0) {
      return <span>I agree to the applicable terms.</span>;
    }

    const parts = [];
    safeLinks.forEach((link, idx) => {
      if (idx > 0) {
        if (idx === safeLinks.length - 1) {
          parts.push(<span key={`sep-${idx}`}> and </span>);
        } else {
          parts.push(<span key={`sep-${idx}`}>, </span>);
        }
      }
      parts.push(
        <MuiLink
          key={`link-${idx}`}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{ color: colors.primary, fontWeight: 500 }}
          onClick={(e) => e.stopPropagation()}
        >
          {link.name}
        </MuiLink>
      );
    });

    return (
      <Typography
        component="span"
        variant="body2"
        sx={{ color: colors.text, lineHeight: 1.4, display: "inline" }}
      >
        I agree to {parts}.
      </Typography>
    );
  };

  const renderVestingLabel = () => (
    <Typography
      component="span"
      variant="body2"
      sx={{ color: colors.text, lineHeight: 1.4, display: "inline" }}
    >
      I agree to the{" "}
      <MuiLink
        component="button"
        underline="hover"
        sx={{
          color: colors.primary,
          fontWeight: 500,
          cursor: "pointer",
          verticalAlign: "baseline",
          background: "none",
          border: "none",
          p: 0,
          fontSize: "inherit",
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setVestingPopupOpen(true);
        }}
      >
        Vesting Schedule
      </MuiLink>
    </Typography>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            backgroundColor: colors.background,
            color: colors.text,
            boxShadow:
              theme === "dark"
                ? "0 8px 32px rgba(0, 0, 0, 0.3)"
                : "0 8px 32px rgba(0, 0, 0, 0.1)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            borderBottom: `1px solid ${colors.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pr: 2,
            position: "relative",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: colors.text,
              fontSize: isMobile ? "1.25rem" : "1.5rem",
            }}
          >
            Terms &amp; Conditions
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: colors.textSecondary,
              position: "absolute",
              right: 16,
              "&:hover": {
                backgroundColor: theme === "dark" ? "#333" : "#f5f5f5",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ py: 4 }}>
          <Typography
            variant="body1"
            sx={{
              color: colors.textSecondary,
              mb: 3,
              lineHeight: 1.6,
              fontSize: isMobile ? "0.9rem" : "1rem",
            }}
          >
            Please review the following document
            {Array.isArray(links) && links.length > 1 ? "s" : ""} and confirm your
            agreement before continuing with this trade.
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {/* Existing terms checkbox */}
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 2,
                border: `1px solid ${colors.border}`,
                backgroundColor: theme === "dark" ? "#2d2d2d" : "#f8f9fa",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    size="small"
                    sx={{
                      p: 0.5,
                      color: colors.primary,
                      "&.Mui-checked": { color: colors.primary },
                    }}
                  />
                }
                label={renderAgreementLabel()}
                sx={{
                  alignItems: "center",
                  m: 0,
                  gap: 1,
                  "& .MuiFormControlLabel-label": { lineHeight: 1.4 },
                }}
              />
            </Box>

            {/* Vesting schedule checkbox — only shown when vestingTableHead/Body are provided */}
            {showVestingSection && (
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  border: `1px solid ${colors.border}`,
                  backgroundColor: theme === "dark" ? "#2d2d2d" : "#f8f9fa",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={vestingAgreed}
                      onChange={(e) => setVestingAgreed(e.target.checked)}
                      size="small"
                      sx={{
                        p: 0.5,
                        color: colors.primary,
                        "&.Mui-checked": { color: colors.primary },
                      }}
                    />
                  }
                  label={renderVestingLabel()}
                  sx={{
                    alignItems: "center",
                    m: 0,
                    gap: 1,
                    "& .MuiFormControlLabel-label": { lineHeight: 1.4 },
                  }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 2,
            borderTop: `1px solid ${colors.border}`,
            justifyContent: "space-between",
            flexDirection: isMobile ? "column-reverse" : "row",
            gap: isMobile ? 2 : 0,
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              borderColor: colors.border,
              color: colors.textSecondary,
              "&:hover": {
                borderColor: colors.primary,
                backgroundColor: `${colors.primary}10`,
              },
              width: isMobile ? "100%" : "auto",
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleProceed}
            variant="contained"
            disabled={!canProceed}
            endIcon={<ArrowForwardIcon sx={{ ml: 1.5 }} />}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              backgroundColor: colors.primary,
              "&:hover": {
                backgroundColor: theme === "dark" ? "#1565c0" : "#115293",
              },
              width: isMobile ? "100%" : "auto",
              gap: 1.5,
            }}
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vesting Schedule popup — mounts independently, does not close Terms popup */}
      {showVestingSection && (
        <VestingSchedulePopup
          open={vestingPopupOpen}
          onClose={() => setVestingPopupOpen(false)}
          theme={theme}
          currency={vestingCurrency}
        />
      )}
    </>
  );
};

export default TermsAgreementPopup;
